import { IconPalette } from "@tabler/icons-react";
import React, { memo } from "react";
import { Alert, Col, Row, Form } from "react-bootstrap";
import { Controller } from "react-hook-form";

const StepLanguage = memo(({ register, watch, setValue, errors, control }) => {
  const currentLanguage = watch("language");
  
  React.useEffect(() => {
    if (!currentLanguage) {
      setValue("language", "default");
    }
  }, [currentLanguage, setValue]);

  const languageOptions = [
    {
      id: "default",
      label: "Identificar Idioma",
      icon: "🌐",
      defaultChecked: true,
    },
    {
      id: "pt-br",
      label: "Português Brasil",
      icon: "🇧🇷",
      defaultChecked: false,
    },
  ];

  const languageStyleOptions = [
    { id: "friendly", label: "Fale como um amigo", icon: "👋" },
    { id: "natural", label: "Use linguagem leve e natural", icon: "🌟" },
    { id: "enthusiastic", label: "Demonstre empolgação", icon: "🎉" },
  ];

  const fundamentalRulesOptions = [
    {
      id: "noMarkdown",
      label: "Não usar formatação markdown",
      icon: "📝",
      defaultChecked: true,
    },
    {
      id: "avoidFormal",
      label: "Evite palavras formais",
      icon: "🚫",
      defaultChecked: true,
    },
    {
      id: "useBreaks",
      label: "Use [[break]] para quebra de mensagens longas",
      icon: "⏸️",
      defaultChecked: false,
    },
  ];

  return (
    <div>
      <Form.Group className="mb-4">
        <Form.Label>Idioma</Form.Label>
        <Row>
          {languageOptions.map((option) => (
            <Col md={6} key={option.id} className="mb-2">
              <div className="d-flex align-items-center">
                <Form.Check
                  type="radio"
                  id={`language-${option.id}`}
                  value={option.id}
                  {...register("language")}
                />
                <Form.Label htmlFor={`language-${option.id}`} className="ms-2 user-select-none" style={{cursor: 'pointer'}}>
                  <span className="me-2">{option.icon}</span>
                  {option.label}
                </Form.Label>
              </div>
            </Col>
          ))}
        </Row>
      </Form.Group>

      <div className="d-flex align-items-center mb-4">
        <IconPalette className="me-2 text-primary" size={24} />
        <h4 className="mb-0">Estilo de Linguagem</h4>
      </div>

      <Form.Group className="mb-4">
        <Form.Label>Opções de Estilo</Form.Label>
        <Controller
          name="languageStyle"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Row>
              {languageStyleOptions.map((option) => (
                <Col md={6} key={option.id} className="mb-2">
                  <div className="d-flex align-items-center">
                    <Form.Check
                      type="checkbox"
                      id={`style-${option.id}`}
                      checked={value?.includes(option.id) || false}
                      onChange={(e) => {
                        const newValue = value || [];
                        if (e.target.checked) {
                          onChange([...newValue, option.id]);
                        } else {
                          onChange(newValue.filter(v => v !== option.id));
                        }
                      }}
                    />
                    <Form.Label htmlFor={`style-${option.id}`} className="ms-2 user-select-none" style={{cursor: 'pointer'}}>
                      <span className="me-2">{option.icon}</span>
                      {option.label}
                    </Form.Label>
                  </div>
                </Col>
              ))}
            </Row>
          )}
        />
      </Form.Group>

      <Form.Group className="mb-4">
        <Form.Label>Regras Fundamentais</Form.Label>
        <Controller
          name="fundamentalRules"
          control={control}
          defaultValue={fundamentalRulesOptions.filter(option => option.defaultChecked).map(option => option.id)}
          render={({ field: { value, onChange } }) => (
            <Row>
              {fundamentalRulesOptions.map((option) => (
                <Col md={6} key={option.id} className="mb-2">
                  <div className="d-flex align-items-center">
                    <Form.Check
                      type="checkbox"
                      id={`rule-${option.id}`}
                      checked={value?.includes(option.id) || false}
                      onChange={(e) => {
                        const newValue = value || [];
                        if (e.target.checked) {
                          onChange([...newValue, option.id]);
                        } else {
                          onChange(newValue.filter(v => v !== option.id));
                        }
                      }}
                    />
                    <Form.Label htmlFor={`rule-${option.id}`} className="ms-2 user-select-none" style={{cursor: 'pointer'}}>
                      <span className="me-2">{option.icon}</span>
                      {option.label}
                    </Form.Label>
                  </div>
                </Col>
              ))}
            </Row>
          )}
        />
      </Form.Group>

      <Alert variant="info" className="mb-4">
        <h6>Exemplos de Linguagem:</h6>
        <div className="row">
          <div className="col-md-6">
            <strong className="text-danger">❌ Evitar:</strong>
            <ul className="small mb-0">
              <li>"Solicito que informe"</li>
              <li>"Gostaria de saber"</li>
              <li>"Por gentileza"</li>
            </ul>
          </div>
          <div className="col-md-6">
            <strong className="text-success">✅ Preferir:</strong>
            <ul className="small mb-0">
              <li>"Me conta aí"</li>
              <li>"Quero saber"</li>
              <li>"Por favor"</li>
            </ul>
          </div>
        </div>
      </Alert>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Palavras Proibidas</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              {...register("prohibitedWords")}
              placeholder="Ex: solicito, cordialmente, atenciosamente..."
            />
            <Form.Text className="text-muted">
              Separe por vírgulas as palavras que o agente deve evitar
            </Form.Text>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Palavras Preferidas</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              {...register("preferredWords")}
              placeholder="Ex: oi, olá, que legal, perfeito..."
            />
            <Form.Text className="text-muted">
              Separe por vírgulas as palavras que o agente deve usar
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
});

StepLanguage.displayName = "StepLanguage";

export default StepLanguage;
